import { createContext, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import  {
  ReactFlow,
  useNodesState,
  useEdgesState,
  ReactFlowProvider,
  Controls,
  Background,
  BezierEdge,
  BackgroundVariant,
  useReactFlow,
} from '@xyflow/react';
import type { NodeProps, Connection as NodeConnection } from '@xyflow/react';
import { Button } from 'antd';
import { CloseOutlined } from '@ant-design/icons';
import { Trans } from '@lingui/macro';

import { BaseNode, CreateNode, DeletableEdge, NodeConfiguration } from '@components';
import { useConnectNodes } from '@hooks';
import { BuilderNodeType, BuilderEdgeType } from '@types';
import { getBuilderNodes, getBuilderEdges } from '@utils';

import styles from './FlowBuilder.module.scss';
import { useNodeResources } from './useNodeResources.query';
import { AppEdge, AppNode } from '../../../types/node';

export const HighlightedNodeContext = createContext<string | null>(null);

type FlowBuilderProps = {
  nodes?: RawNode[] | null;
  getNodeClassName?: (node: RawNode) => string;
  highlightedNodeUuid?: string | null;
  editable?: boolean;
  defaultZoom?: number;
  fitView?: boolean;
  fitViewOptions?: {
    padding?: number;
    maxZoom?: number;
    minZoom?: number;
  };
  onExternalHoverNode?: (uuid: string | null) => void;
};

const FlowBuilder = ({
  nodes,
  editable = true,
  getNodeClassName,
  highlightedNodeUuid = null,
  defaultZoom = 1,
  fitView = true,
  fitViewOptions,
  onExternalHoverNode,
}: FlowBuilderProps) => {
  const { data } = useNodeResources();
  const { node_type_resources: nodeTypes, action_resources: nodeActions } = data || {};

  const [builderNodes, setBuilderNodes, onBuilderNodesChange] = useNodesState<AppNode>(
    [],
  );

  const [builderEdges, setBuilderEdges, onBuilderEdgesChange] = useEdgesState<AppEdge>(
    [],
  );

  const reactFlow = useReactFlow();
  const hasInitialFitRef = useRef(false);

  useEffect(() => {
    if (!fitView || hasInitialFitRef.current) return;
    if (builderNodes.length === 0) return;
    const id = window.setTimeout(() => {
      reactFlow.fitView({ ...fitViewOptions, duration: 0 });
      hasInitialFitRef.current = true;
    }, 200);
    return () => window.clearTimeout(id);
  }, [fitView, builderNodes.length, reactFlow, fitViewOptions]);

  useEffect(() => {
    if (nodeTypes && nodeActions && nodes) {
      setBuilderNodes((existingBuilderNodes) => {
        return getBuilderNodes({
          nodes,
          builderNodes: existingBuilderNodes,
          editable,
          getNodeClassName,
          nodeTypes,
          nodeActions,
        });
      });
      setBuilderEdges(() =>
        getBuilderEdges({
          nodes,
          editable,
        }),
      );
    }
  }, [
    nodes,
    editable,
    getNodeClassName,
    setBuilderNodes,
    nodeTypes,
    nodeActions,
    setBuilderEdges,
  ]);

  const onCloseToolbar = useCallback((uuid: string) => {
    setBuilderNodes((nodes) =>
      nodes.map((n) =>
        n.data.uuid === uuid ? { ...n, selected: false } : n,
      ),
    );
  }, [setBuilderNodes]);

  const selectedNode = useMemo(
    () => builderNodes.find((n) => n.selected && n.data.editable) ?? null,
    [builderNodes],
  );

  const [displayedNode, setDisplayedNode] = useState<AppNode | null>(null);

  useEffect(() => {
    if (selectedNode) {
      setDisplayedNode(selectedNode);
      return;
    }
    const timeout = window.setTimeout(() => setDisplayedNode(null), 250);
    return () => window.clearTimeout(timeout);
  }, [selectedNode]);

  const onHoverEdge = (edgeId: string, hovered: boolean) => {
    setBuilderEdges((builderEdges) => {
      return builderEdges.map((edge) => {
        if (edge.id === edgeId) {
          return {
            ...edge,
            data: { ...edge.data, hovered },
          };
        } else {
          return edge;
        }
      });
    });
  };

  const onHoverNode = useCallback((nodeUuid: string | null, hovered: boolean) => {
    setBuilderEdges((edges) =>
      edges.map((edge): AppEdge => {
        // Highlight only outgoing edges (where this node is the source)
        if (edge.source === nodeUuid) {
          return {
            ...edge,
            data: { hovered: false, ...edge.data, nodeHovered: hovered },
          };
        }
        return edge;
      })
    );
    // Notify external components about the hover state
    onExternalHoverNode?.(hovered ? nodeUuid : null);
  }, [setBuilderEdges, onExternalHoverNode]);

  const connectNodes = useConnectNodes();
  const onConnect = ({ source, target }: NodeConnection) => {
    connectNodes(source as string, target as string);
  };

  const builderNodeTypes = useMemo(() => {
    return {
      [BuilderNodeType.baseNode]: (props: NodeProps<AppNode>) => (
        <BaseNode
          {...props}
          data={{
            ...props.data,
            editable,
            onHoverNode,
          }}
        />
      ),
      [BuilderNodeType.createNode]: CreateNode,
    };
  }, [editable, onHoverNode]);

  const builderEdgeTypes = useMemo(() => {
    return {
      [BuilderEdgeType.baseEdge]: BezierEdge,
      [BuilderEdgeType.deletableEdge]: DeletableEdge,
    };
  }, []);

  return (
    <HighlightedNodeContext.Provider value={highlightedNodeUuid}>
      <div className={styles.container} data-testid="flow-builder-container">
        <ReactFlow
          nodes={builderNodes}
          edges={builderEdges}
          onNodesChange={editable ? onBuilderNodesChange : undefined}
          onEdgesChange={editable ? onBuilderEdgesChange : undefined}
          nodeTypes={builderNodeTypes}
          edgeTypes={builderEdgeTypes}
          // Add edge hovering state
          onEdgeMouseEnter={(_, edge) => onHoverEdge(edge.id, true)}
          onEdgeMouseLeave={(_, edge) => onHoverEdge(edge.id, false)}
          // Disable dragging if not editable
          nodesDraggable={editable}
          // Disable node selection and connections if not editable
          nodesConnectable={editable}
          elementsSelectable={true}  // Always allow hover, even in read-only mode
          // Nodes have a dragHandle selector, so canvas pan and node drag don't conflict
          panOnDrag
          zoomOnScroll={!editable}
          panOnScroll={!editable}
          zoomOnDoubleClick={!editable}
          nodesFocusable={false}
          // This makes it so node toolbar doesn't get displayed when dragging
          selectNodesOnDrag={false}
          // Increase drag threshold - only start dragging after moving 5px
          // This makes clicks more reliable
          nodeDragThreshold={5}
          // On node connection - only allow if editable
          onConnect={editable ? onConnect : undefined}
          // Disable edge selection
          // Disable all keyboard controls
          deleteKeyCode={null}
          selectionKeyCode={null}
          multiSelectionKeyCode={null}
          disableKeyboardA11y
          // Zoom configuration
          fitView={fitView}
          fitViewOptions={fitViewOptions}
          defaultViewport={{ x: 0, y: 0, zoom: defaultZoom }}
          // Hide attribution
          proOptions={{ hideAttribution: true }}
        >
          <Controls />
          <Background
            gap={25}
            size={1}
            variant={BackgroundVariant.Cross}
            bgColor='#fafafa'
          />
        </ReactFlow>
        <div className={styles.sidePanel} data-open={!!selectedNode}>
          {displayedNode && (
            <>
              <div className={styles.sidePanelHeader}>
                <div>
                  <Trans>Configure app</Trans>
                </div>
                <Button
                  className={styles.sidePanelCloseButton}
                  type="text"
                  size="small"
                  shape="circle"
                  icon={<CloseOutlined />}
                  onClick={() => onCloseToolbar(displayedNode.data.uuid)}
                />
              </div>
              <div className={styles.sidePanelContent}>
                <NodeConfiguration
                  key={displayedNode.data.uuid}
                  data={displayedNode.data}
                />
              </div>
            </>
          )}
        </div>
      </div>
    </HighlightedNodeContext.Provider>
  );
};

const FlowBuilderContainer = (props: FlowBuilderProps) => {
  return (
    <ReactFlowProvider>
      <FlowBuilder {...props} />
    </ReactFlowProvider>
  );
};

export default FlowBuilderContainer;
